import BottomSheet from '../bottom-sheet/BottomSheet';
import dayjs, { Dayjs } from 'dayjs';
import Loader from '../Loader';
import logger from '../../utils/logger';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { breakpoints } from '../../theme/breakpoints';
import { buildSavingsMessage, calculateBestRate, RateCalculationResult } from '../../utils/quickEstimateRate';
import { checkInvalidStartDate } from '../../utils/cart';
import { cleanPhoneNumber, displayStringToDialString } from '../../utils/phoneUtils';
import { closeQuickEstimate, openQuickEstimateThankYou, QuickEstimateConfig } from '../../redux/slice/quickEstimateSlice';
import { DataLayerEventName } from '../../types/data-layer';
import { pushEventToDataLayer } from '../../utils/dataLayerUtils';
import { Controller, useForm } from 'react-hook-form';
import { DEFAULT_PHONE_NUMBER } from '../../constants/phone';
import { LD_FLAGS } from '../../constants/launch-darkley';
import { getPriceCatalogKey, isPriceInLoadingStage } from '../../utils/equipment';
import { isValidFourWeekPrice } from '../../utils/price';
import { LocationStatus, type Branch } from '../../types/location';
import { Price, PriceEntry, PriceStatus } from '../../types/equipment';
import { palette } from '../../theme/palette';
import { getReCaptchaToken } from '../../utils/reCaptcha';
import { isAxiosError } from 'axios';
import { messageContents } from '../../constants/message';
import { openMessageModal } from '../../utils/message';
import ReCaptchaProvider from '../recaptcha/ReCaptchaProvider';
import ReCaptchaV2Challenge from '../recaptcha/ReCaptchaV2Challenge';
import {
  isRentalRecaptchaChallengeResponse,
  useRentalRecaptchaV2Fallback,
} from '../../hooks/useRentalRecaptchaV2Fallback';
import { quickEstimateContactSchema, QuickEstimateContactValues } from './quickEstimateFormSchema';
import { QuickEstimatePayload } from '../../types/quick-estimate';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import { submitQuickEstimate } from '../../apis/quickEstimate';
import { resolveLeadRoutingPhone } from '../../utils/leadRouting';
import { useBranchSync } from '../../hooks/useBranchSync';
import type { LeadRoutingConfig } from '../../types/lead-routing';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BestRateLabel,
  BestRateValue,
  BottomSheetCloseButton,
  BottomSheetContentWrapper,
  BottomSheetLabel,
  BottomSheetTopBar,
  BranchPhoneLink,
  ChangeDatesLink,
  CloseButton,
  CloseIcon,
  ContactHeading,
  ContactSection,
  ContactSubtext,
  ContentWrapper,
  ContinueLoaderSlot,
  DateRangePicker,
  Disclaimer,
  FormField,
  GetEstimateButton,
  HelperText,
  Label,
  MissingPriceMessage,
  MissingPriceMessagePhoneLink,
  MissingPriceMessageWrapper,
  Modal,
  ModalContent,
  StandardRateValue,
  RateCard,
  RateRow,
  RentalDuration,
  RequestRentalButton,
  SameDayNotice,
  SameDayNoticeIcon,
  SameDayNoticeText,
  SavingsMessageBox,
  SavingsMessageText,
  Subtitle,
  Title,
  ValidationMessage,
} from './QuickEstimateModalStyle';

enum DependenciesStatus {
  LOADING = 'loading',
  FAILED = 'failed',
  READY = 'ready',
}

// Single source of truth for what the Continue button can do right now:
// - LOADING → location lookup or price fetch is still in flight (or no
//   price entry has been seeded yet).
// - FAILED  → location or price has settled to a state that cannot
//   produce an estimate (price unknown / unusable four-week price).
// - READY   → branch resolved, price entry valid → user can advance.
function deriveDependenciesStatus(
  locationStatus: LocationStatus | null,
  priceEntry: PriceEntry | undefined,
): DependenciesStatus {
  if (locationStatus === LocationStatus.IS_LOADING) return DependenciesStatus.LOADING;
  if (!priceEntry || isPriceInLoadingStage(priceEntry.status)) return DependenciesStatus.LOADING;
  if (priceEntry.status === PriceStatus.ERROR || priceEntry.status === PriceStatus.PRICE_NOT_FOUND) {
    return DependenciesStatus.FAILED;
  }
  if (
    priceEntry.status === PriceStatus.COMPLETED &&
    !isValidFourWeekPrice(priceEntry.price?.price_per_month)
  ) {
    return DependenciesStatus.FAILED;
  }
  return DependenciesStatus.READY;
}

dayjs.extend(utc);
dayjs.extend(tz);

type ModalStep = 'dates' | 'estimate';

enum ModalVisibility {
  NONE = 'NONE',
  CENTERED_MODAL = 'CENTERED_MODAL',
  BOTTOM_SHEET = 'BOTTOM_SHEET',
}

type DateErrorState = {
  startDateError?: boolean;
  endDateError?: boolean;
  endDateMessage?: string;
};

export default function QuickEstimateModal() {
  const dispatch = useAppDispatch();
  const config = useAppSelector((state: RootState) => state.quickEstimate.config);
  const branch = useAppSelector((state: RootState) => state.location.branch);
  const ldFlags = useAppSelector((state: RootState) => state.app?.ldFlags);
  const locationStatus = useAppSelector((state: RootState) => state.location.status);
  const priceCatalog = useAppSelector((state: RootState) => state.prices.priceCatalog);
  const cartTimezone = useAppSelector((state: RootState) => state.cart.timezone);
  const priceEntry = config
    ? priceCatalog[getPriceCatalogKey(config.equipmentId)]
    : undefined;
  const dependenciesStatus = deriveDependenciesStatus(locationStatus, priceEntry);
  const { isBranchSyncing, awaitBranchSync } = useBranchSync(branch, Boolean(config?.open));

  const [visibility, setVisibility] = useState<ModalVisibility>(ModalVisibility.NONE);
  const [step, setStep] = useState<ModalStep>('dates');
  const [startDate, setStartDate] = useState<Dayjs | null | unknown>(null);
  const [endDate, setEndDate] = useState<Dayjs | null | unknown>(null);
  const [dateErrorState, setDateErrorState] = useState<DateErrorState | null>(null);
  const [rateResult, setRateResult] = useState<RateCalculationResult | null>(null);
  const [resolvedPrice, setResolvedPrice] = useState<Price | null>(null);
  const [isSameDayRental, setIsSameDayRental] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    showRecaptchaV2,
    recaptchaV2Ref,
    showRecaptchaV2Challenge,
    dismissRecaptchaV2Challenge,
  } = useRentalRecaptchaV2Fallback();

  const minStartDate = dayjs().local().startOf('day');

  const defaultBranchPhone = branch?.location?.location_phone ?? DEFAULT_PHONE_NUMBER;
  const branchPhone = resolveLeadRoutingPhone(
    ldFlags?.[LD_FLAGS.LEAD_ROUTING_CONFIG] as LeadRoutingConfig | undefined,
    branch?.location?.region?.region_id,
    defaultBranchPhone,
  );
  const cleanedBranchPhone = cleanPhoneNumber(branchPhone);

  const methods = useForm<QuickEstimateContactValues>({
    defaultValues: { email: '', phone: '' },
    resolver: zodResolver(quickEstimateContactSchema),
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = methods;

  const formError = (errors as Record<string, { message?: string }>)?.['_form']?.message;

  useEffect(() => {
    if (!config || !config.open) {
      setVisibility(ModalVisibility.NONE);
    } else {
      setVisibility(
        window.innerWidth <= breakpoints.values.sm ? ModalVisibility.BOTTOM_SHEET : ModalVisibility.CENTERED_MODAL,
      );
    }
  }, [config]);

  useEffect(() => {
    if (visibility === ModalVisibility.NONE) {
      setStartDate(null);
      setEndDate(null);
      setDateErrorState(null);
      setStep('dates');
      setRateResult(null);
      setResolvedPrice(null);
      setIsSameDayRental(false);
      setIsSubmitting(false);
      dismissRecaptchaV2Challenge();
      resetForm();
    }
  }, [visibility, resetForm, dismissRecaptchaV2Challenge]);

  useEffect(() => {
    setDateErrorState((prev) => ({
      startDateError: startDate ? false : prev?.startDateError,
      endDateError: endDate ? false : prev?.endDateError,
    }));

    if (startDate) {
      const startUnix = dayjs(String(startDate)).unix();
      setIsSameDayRental(checkInvalidStartDate(startUnix, cartTimezone));
    } else {
      setIsSameDayRental(false);
    }
  }, [startDate, endDate, cartTimezone]);

  const handleClose = () => {
    dispatch(closeQuickEstimate());
  };

  const validateDates = (): boolean => {
    const isValid = !!startDate && !!endDate;
    setDateErrorState({ startDateError: !startDate, endDateError: !endDate });
    return isValid;
  };

  const handleGetEstimate = () => {
    if (!validateDates() || !config) return;

    const start = dayjs(String(startDate)).utc().tz(cartTimezone);
    const end = dayjs(String(endDate)).utc().tz(cartTimezone);
    const rentalDays = end.diff(start, 'day') + 1;

    if (rentalDays <= 0) {
      setDateErrorState({ startDateError: false, endDateError: true, endDateMessage: 'End date must be on or after the start date' });
      return;
    }

    const price = priceEntry?.price;

    if (!price) {
      return;
    }

    const result = calculateBestRate({
      pricePerDay: price.price_per_day ?? 0,
      pricePerWeek: price.price_per_week ?? 0,
      pricePerMonth: price.price_per_month ?? 0,
      rentalDays,
    });

    setRateResult(result);
    setResolvedPrice(price);
    setStep('estimate');
  };

  const handleChangeDates = () => {
    setStep('dates');
    setRateResult(null);
    setResolvedPrice(null);
    dismissRecaptchaV2Challenge();
    resetForm();
  };

  const buildPayload = (
    confirmedConfig: QuickEstimateConfig,
    confirmedRateResult: RateCalculationResult,
    confirmedPrice: Price,
    formData: QuickEstimateContactValues,
    submitBranch: Branch | null | undefined,
    recaptchaToken: string,
    recaptchaVersion?: 'v2',
  ): QuickEstimatePayload => {
    const start = dayjs(String(startDate)).utc().tz(cartTimezone);
    const end = dayjs(String(endDate)).utc().tz(cartTimezone);

    const branchCity = submitBranch?.location?.city?.trim() ?? '';
    const branchState = submitBranch?.location?.state?.trim() ?? '';
    let branchName = '';
    if (branchCity && branchState) branchName = `EquipmentShare ${branchCity}, ${branchState}`;
    else if (branchCity || branchState) branchName = `EquipmentShare ${branchCity || branchState}`;

    return {
      equipment_class_id: String(confirmedConfig.equipmentId),
      equipment_class_name: confirmedConfig.equipmentName,
      equipment_class_image: confirmedConfig.equipmentClassImage,
      category: confirmedConfig.categoryName || null,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
      branch_directions_link: submitBranch?.location?.get_directions_link ?? '',
      branch_id: String(submitBranch?.branch_id ?? ''),
      branch_name: branchName,
      branch_phone: cleanPhoneNumber(submitBranch?.location?.location_phone) ?? '',
      branch_region_id: submitBranch?.location?.region?.region_id,
      charged_total: confirmedRateResult.standardRate,
      optimized_total: confirmedRateResult.bestRate,
      weekly_rate: confirmedPrice.price_per_week ?? 0,
      daily_rate: confirmedPrice.price_per_day ?? 0,
      '4_weekly_rate': confirmedPrice.price_per_month ?? 0,
      optimized_breakdown: {
        '4_week_periods': confirmedRateResult.bestBreakdown.months,
        weeks: confirmedRateResult.bestBreakdown.weeks,
        days: confirmedRateResult.bestBreakdown.days,
      },
      landing_page: window.location.href,
      ...(formData.phone ? { mobile_number: cleanPhoneNumber(formData.phone) ?? '' } : {}),
      ...(formData.email ? { email_address: formData.email } : {}),
      timezone: cartTimezone,
      recaptchaToken,
      ...(recaptchaVersion === 'v2' && { recaptchaVersion: 'v2' }),
    };
  };

  const handleSubmitSuccess = (equipmentName: string) => {
    dispatch(closeQuickEstimate());
    dispatch(openQuickEstimateThankYou({ equipmentName }));
  };

  const handleSubmitError = (error: unknown, equipmentName: string, isV2Retry: boolean) => {
    if (isAxiosError(error) && error.response) {
      const status = error.response.status;

      if (status === 409) {
        handleSubmitSuccess(equipmentName);
        return;
      }

      if (isRentalRecaptchaChallengeResponse(status, error.response.data)) {
        if (isV2Retry) {
          recaptchaV2Ref.current?.reset();
          openMessageModal(messageContents.quickEstimateRecaptchaError);
        } else {
          showRecaptchaV2Challenge();
        }
        return;
      }

      if (status === 400) {
        const responseMessage: string = (error.response.data as { message?: string })?.message ?? '';
        if (responseMessage.toLowerCase().includes('recaptcha')) {
          openMessageModal(messageContents.quickEstimateRecaptchaError);
        } else {
          openMessageModal(messageContents.quickEstimateValidationError);
        }
        return;
      }

      if (status === 422) {
        openMessageModal(messageContents.quickEstimateSubmissionError);
        return;
      }

      if (status >= 500) {
        openMessageModal(messageContents.quickEstimateServerError);
        return;
      }
    }

    openMessageModal(messageContents.formSubmissionError);
  };

  const onSubmitContact = async (
    formData: QuickEstimateContactValues,
    options?: { recaptchaVersion?: 'v2'; recaptchaToken?: string },
  ) => {
    if (!config || !rateResult || !resolvedPrice || !startDate || !endDate || isSubmitting) return;

    const isV2Retry = options?.recaptchaVersion === 'v2';

    setIsSubmitting(true);
    try {
      const recaptchaToken = options?.recaptchaToken ?? (await getReCaptchaToken('submit'));
      if (!recaptchaToken) {
        if (!isV2Retry) {
          showRecaptchaV2Challenge();
          return;
        }
        logger('error', 'QuickEstimate: undefined recaptcha token');
        openMessageModal(messageContents.formSubmissionError);
        return;
      }

      const branch = await awaitBranchSync();

      const payload = buildPayload(
        config,
        rateResult,
        resolvedPrice,
        formData,
        branch,
        recaptchaToken,
        isV2Retry ? 'v2' : undefined,
      );
      await submitQuickEstimate(payload);
      dismissRecaptchaV2Challenge();

      pushEventToDataLayer({
        type: DataLayerEventName.QUICK_ESTIMATE_SUBMITTED,
        payload: {
          form_type: 'request_availability',
          equipment_class_id: payload.equipment_class_id,
          equipment_class_name: payload.equipment_class_name,
          category: payload.category,
          optimized_total: payload.optimized_total,
          charged_total: payload.charged_total,
          daily_rate: payload.daily_rate,
          weekly_rate: payload.weekly_rate,
          monthly_rate: payload['4_weekly_rate'],
          rental_duration_days: rateResult.rentalDays,
          start_date: payload.start_date,
          end_date: payload.end_date,
          branch_id: payload.branch_id,
          branch_name: payload.branch_name,
          email: formData.email,
          phone: formData.phone,
        },
      });

      handleSubmitSuccess(config.equipmentName);
    } catch (error) {
      logger('error', 'at QuickEstimateModal while submitting equipment availability request', error);
      handleSubmitError(error, config.equipmentName, isV2Retry);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRecaptchaV2Verify = (token: string) => {
    void handleSubmit((formData) =>
      onSubmitContact(formData, { recaptchaVersion: 'v2', recaptchaToken: token }),
    )();
  };

  if (!config) return null;

  const title = `Get an estimate now - ${config.equipmentName}`;

  const dateStepContent = (
    <>
      <Subtitle>When do you need the equipment?</Subtitle>
      <DateRangePicker
        minStartDate={minStartDate}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        startDateLabel={<Label>Start Date</Label>}
        endDateLabel={<Label>End Date</Label>}
        startDateHelperLabel={
          dateErrorState?.startDateError ? (
            <HelperText hasError>Start date required</HelperText>
          ) : (
            <></>
          )
        }
        endDateHelperLabel={
          dateErrorState?.endDateError ? (
            <HelperText hasError>{dateErrorState?.endDateMessage ?? 'End date required'}</HelperText>
          ) : (
            <></>
          )
        }
        startDateError={dateErrorState?.startDateError}
        endDateError={dateErrorState?.endDateError}
      />
      {isSameDayRental && (
        <SameDayNotice>
          <SameDayNoticeIcon />
          <SameDayNoticeText>
            <strong>Heads up:</strong> Same-day rentals may have limited availability. For fastest
            service, call{' '}
            <BranchPhoneLink href={`tel:${cleanedBranchPhone}`}>{branchPhone}</BranchPhoneLink>.
          </SameDayNoticeText>
        </SameDayNotice>
      )}
      {dependenciesStatus === DependenciesStatus.FAILED && (
        <MissingPriceMessageWrapper>
          <MissingPriceMessage>
            {messageContents.missingEquipmentPriceEstimate.description}
            {' '}
            <MissingPriceMessagePhoneLink href={`tel:${displayStringToDialString(DEFAULT_PHONE_NUMBER)}`}>
              {DEFAULT_PHONE_NUMBER}
            </MissingPriceMessagePhoneLink>
          </MissingPriceMessage>
        </MissingPriceMessageWrapper>
      )}
      <GetEstimateButton
        onClick={handleGetEstimate}
        disabled={dependenciesStatus !== DependenciesStatus.READY}
        aria-label='Continue'
      >
        {dependenciesStatus === DependenciesStatus.LOADING ? (
          <ContinueLoaderSlot>
            <Loader hexColourCode={palette.primary.contrastText} />
          </ContinueLoaderSlot>
        ) : (
          'CONTINUE'
        )}
      </GetEstimateButton>
    </>
  );

  const savingsMessage = rateResult ? buildSavingsMessage(rateResult) : null;

  const estimateStepContent = rateResult && (
    <>
      <RateCard>
        <RateRow>
          <BestRateLabel>Your Rate:</BestRateLabel>
          <BestRateValue>${rateResult.bestRate.toFixed(2)}*</BestRateValue>
          {rateResult.bestRate < rateResult.standardRate && (
            <StandardRateValue>${rateResult.standardRate.toFixed(2)}</StandardRateValue>
          )}
          <RentalDuration>({rateResult.rentalDays}-day rental)</RentalDuration>
        </RateRow>
        {savingsMessage && (
          <SavingsMessageBox>
            <SavingsMessageText>✅ {savingsMessage}</SavingsMessageText>
          </SavingsMessageBox>
        )}
        <ChangeDatesLink onClick={handleChangeDates}>Change Dates</ChangeDatesLink>
        <Disclaimer>
          * Please note: This is just an estimate. Taxes and delivery are not included and final
          pricing may vary.
        </Disclaimer>
      </RateCard>

      <ContactSection>
        <div>
          <ContactHeading>Ready to confirm availability?</ContactHeading>
          <ContactSubtext>
            Call{' '}
            {isBranchSyncing ? (
              <Loader hexColourCode={palette.custom.darkGrey} />
            ) : (
              <BranchPhoneLink href={`tel:${cleanedBranchPhone}`}>{branchPhone}</BranchPhoneLink>
            )}{' '}
            or leave your email or phone below.
          </ContactSubtext>
        </div>

        <div>
          <Label>Email Address</Label>
          <Controller
            name='email'
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                placeholder='you@email.com'
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </div>

        <div>
          <Label>Phone Number</Label>
          <Controller
            name='phone'
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                placeholder='(555) 555-5555'
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </div>

        {formError && <ValidationMessage>{formError}</ValidationMessage>}

        {showRecaptchaV2 && (
          <ReCaptchaV2Challenge
            ref={recaptchaV2Ref}
            onVerify={onRecaptchaV2Verify}
            onExpired={() => recaptchaV2Ref.current?.reset()}
            onError={() => recaptchaV2Ref.current?.reset()}
          />
        )}

        <RequestRentalButton
          onClick={handleSubmit((formData) => onSubmitContact(formData))}
          disabled={isSubmitting || showRecaptchaV2}
        >
          {isSubmitting ? 'SUBMITTING...' : 'GET ESTIMATE'}
        </RequestRentalButton>
      </ContactSection>
    </>
  );

  const currentContent = step === 'dates' ? dateStepContent : estimateStepContent;

  return (
    <ReCaptchaProvider>
      <Modal
        open={visibility === ModalVisibility.CENTERED_MODAL}
        aria-labelledby='quick-estimate-modal-title'
      >
        <ModalContent>
          <ContentWrapper>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
            <Title id='quick-estimate-modal-title'>{title}</Title>
            {currentContent}
          </ContentWrapper>
        </ModalContent>
      </Modal>

      <BottomSheet
        isOpen={visibility === ModalVisibility.BOTTOM_SHEET}
        onClose={handleClose}
        dialogTitle={false}
      >
        <BottomSheetTopBar>
          <BottomSheetLabel>Quick Estimate</BottomSheetLabel>
          <BottomSheetCloseButton onClick={handleClose}>
            <CloseIcon />
          </BottomSheetCloseButton>
        </BottomSheetTopBar>
        <BottomSheetContentWrapper>
          <Title>{title}</Title>
          {currentContent}
        </BottomSheetContentWrapper>
      </BottomSheet>
    </ReCaptchaProvider>
  );
}
